/**
 * Shipping-provider presentation registry (single source of truth for the UI).
 *
 * The provider id union is INFERRED from the backend router output, so the
 * frontend never forks the enum — adding a provider id on the backend without
 * a presentation row here is a compile error (the Record below is exhaustive).
 *
 * `status: 'coming_soon'` drives the disabled "Coming soon" treatment for
 * future providers that are modelled but not yet shippable.
 * Both the settings picker (Surface A) and the packing station (Surface B)
 * consume this registry so the two surfaces can never disagree.
 *
 * Label-return providers (`isLabelReturn: true`) MUST also carry their packing
 * copy and reprint mode: the type is a union on `isLabelReturn`, so a new
 * label-return provider without them does not compile. The packing station
 * reads these instead of branching on a provider id, so it never shows one
 * carrier's wording for another's order.
 */

import type { RouterOutputs } from '@/lib/trpc';

/** Inferred provider union: 'manual' | 'starshipit' | 'yunexpress' | 'portless' | 'handover'. */
export type ShippingProvider =
  RouterOutputs['productionSettings']['getLocationSettings'][number]['shippingProvider'];

/**
 * How the packing station gets a printed label back after the order shipped:
 *   - `portless`  re-pull the cross-dock PDF (`shipping.reprintPortlessLabel`);
 *   - `api`       re-download the carrier's label by order
 *                 (`shipping.reprintShippingLabel`; YunExpress);
 *   - `dashboard` no API re-fetch, open the carrier's own dashboard (StarShipIt).
 */
export type ShippingReprintMode = 'portless' | 'api' | 'dashboard';

/** `production` namespace i18n keys the packing station shows for a provider. */
export interface ShippingProviderPackingCopy {
  /** Announced while the label is being bought or pulled. */
  sending: string;
  /** Location ships this provider but the org has saved no credentials. */
  notConfigured: string;
  /** Guidance under a credential failure. */
  checkCredentials: string;
  /** Success-card line when the order had already shipped. */
  alreadyShipped: string;
}

interface ShippingProviderPresentationBase {
  /** Display name. Brand casing matters: "StarShipIt", not "Starshipit". */
  label: string;
  /** Short sub-line shown under the label in the dropdown. */
  tagline: string;
  /** 'ga' = selectable today; 'coming_soon' = modelled, not yet shippable. */
  status: 'ga' | 'coming_soon';
  /**
   * Where the provider can be picked. 'client_only' providers (Hand over) are
   * a per-client choice at a shared location and never a location default,
   * so the location-level picker hides them. Omitted = everywhere.
   */
  scope?: 'all' | 'client_only';
  /** Whether the provider needs org-level credentials before it can ship. */
  requiresOrgCreds: boolean;
  /** Deep-link to where the org configures this provider's credentials. */
  credsHref?: string;
}

export type ShippingProviderPresentation =
  | (ShippingProviderPresentationBase & {
      /** No-API provider (Manual): the packing station offers Mark complete. */
      isLabelReturn: false;
    })
  | (ShippingProviderPresentationBase & {
      /**
       * Returns a printable label via API at packing time — drives the
       * packing station's label-provider branch.
       */
      isLabelReturn: true;
      packing: ShippingProviderPackingCopy;
      reprint: ShippingReprintMode;
    });

/** Registry-driven check for the packing station's label-provider branch. */
export function isLabelReturnProvider(provider: ShippingProvider | undefined): boolean {
  return provider ? SHIPPING_PROVIDER_PRESENTATION[provider]?.isLabelReturn ?? false : false;
}

/**
 * Exhaustive presentation map keyed by the inferred provider union.
 */
export const SHIPPING_PROVIDER_PRESENTATION: Record<ShippingProvider, ShippingProviderPresentation> = {
  manual: {
    label: 'Manual',
    tagline: 'Ship outside the IMS',
    status: 'ga',
    requiresOrgCreds: false,
    isLabelReturn: false,
  },
  starshipit: {
    label: 'StarShipIt',
    tagline: 'Generate + print labels at packing',
    status: 'ga',
    requiresOrgCreds: true,
    isLabelReturn: true,
    credsHref: '/production/settings?area=locations#starshipit',
    packing: {
      sending: 'shipping.sendingToStarshipit',
      notConfigured: 'shipping.starshipitNotConfigured',
      checkCredentials: 'shipping.checkCredentials',
      alreadyShipped: 'shipping.labelCreatedEarlier',
    },
    reprint: 'dashboard',
  },
  yunexpress: {
    label: 'YunExpress',
    tagline: 'China shipping labels, printed at packing',
    status: 'ga',
    requiresOrgCreds: true,
    isLabelReturn: true,
    credsHref: '/production/settings?area=locations#yunexpress',
    packing: {
      sending: 'shipping.sendingToYunexpress',
      notConfigured: 'shipping.yunexpressNotConfigured',
      checkCredentials: 'shipping.checkYunexpressCredentials',
      alreadyShipped: 'shipping.yunexpressLabelAlreadyBought',
    },
    reprint: 'api',
  },
  portless: {
    label: 'Portless',
    tagline: 'Print first-mile label; Portless re-labels + ships',
    status: 'ga',
    requiresOrgCreds: true,
    isLabelReturn: true,
    credsHref: '/production/settings?area=locations#portless',
    packing: {
      sending: 'shipping.sendingToPortless',
      notConfigured: 'shipping.portlessNotConfigured',
      checkCredentials: 'shipping.checkPortlessCredentials',
      alreadyShipped: 'shipping.portlessLabelAlreadyPulled',
    },
    reprint: 'portless',
  },
  handover: {
    label: 'Hand over',
    tagline: 'Give the order to the client, no label',
    status: 'ga',
    scope: 'client_only',
    requiresOrgCreds: false,
    isLabelReturn: false,
  },
};

/** True when the provider may only be picked per client, never as a location default. */
export function isClientOnlyProvider(provider: ShippingProvider): boolean {
  return SHIPPING_PROVIDER_PRESENTATION[provider]?.scope === 'client_only';
}

/** Stable display order in the picker (GA providers first, then coming-soon). */
export const SHIPPING_PROVIDER_ORDER: readonly ShippingProvider[] = [
  'manual',
  'starshipit',
  'portless',
  'yunexpress',
  'handover',
];
